import React, { useState, useEffect, useCallback, useRef } from 'react';
import { businessAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import './BusinessSettings.css';

const BusinessSettings = () => {
  const [activeTab, setActiveTab] = useState('providers');

  // Providers State
  const [providers, setProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(false);

  // Jurisdictions State
  const [jurisdictions, setJurisdictions] = useState([]);
  const [jurisdictionsLoading, setJurisdictionsLoading] = useState(false);

  // Activities State
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [selectedJurisdiction, setSelectedJurisdiction] = useState(null);
  const [jurisdictionDropdownOpen, setJurisdictionDropdownOpen] = useState(false);
  const jurisdictionDropdownRef = useRef(null);

  // Modal State
  const [modal, setModal] = useState({
    isOpen: false,
    type: '', // 'provider', 'jurisdiction', 'activity'
    mode: '', // 'create', 'edit'
    data: null
  });

  // Form State
  const [formData, setFormData] = useState({});
  const [formError, setFormError] = useState('');

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (jurisdictionDropdownRef.current && !jurisdictionDropdownRef.current.contains(event.target)) {
        setJurisdictionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch Providers
  const fetchProviders = useCallback(async () => {
    try {
      setProvidersLoading(true);
      const response = await businessAPI.getProviders();
      if (response.data.success) {
        setProviders(response.data.data.providers || response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch providers:', error);
    } finally {
      setProvidersLoading(false);
    }
  }, []);

  // Fetch Jurisdictions
  const fetchJurisdictions = useCallback(async () => {
    try {
      setJurisdictionsLoading(true);
      const response = await businessAPI.getJurisdictions();
      if (response.data.success) {
        const data = response.data.data.jurisdictions || response.data.data;
        setJurisdictions(data);
        if (!selectedJurisdiction && data.length > 0) {
          setSelectedJurisdiction(data[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch jurisdictions:', error);
    } finally {
      setJurisdictionsLoading(false);
    }
  }, [selectedJurisdiction]);

  // Fetch Activities
  const fetchActivities = useCallback(async () => {
    if (!selectedJurisdiction) return;
    try {
      setActivitiesLoading(true);
      const response = await businessAPI.getActivities({ jurisdiction_id: selectedJurisdiction.id });
      if (response.data.success) {
        setActivities(response.data.data.activities || response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      setActivitiesLoading(false);
    }
  }, [selectedJurisdiction]);

  useEffect(() => {
    fetchProviders();
    fetchJurisdictions();
  }, []);

  useEffect(() => {
    if (activeTab === 'providers') fetchProviders();
    if (activeTab === 'jurisdictions') fetchJurisdictions();
    if (activeTab === 'activities') fetchActivities();
  }, [activeTab, fetchProviders, fetchJurisdictions, fetchActivities]);

  useEffect(() => {
    if (activeTab === 'activities' && selectedJurisdiction) {
      fetchActivities();
    }
  }, [selectedJurisdiction]);

  // Modal Handlers
  const openModal = (type, mode, data = null) => {
    setFormError('');
    if (mode === 'edit' && data) {
      if (type === 'provider') {
        setFormData({
          name: data.name,
          code: data.code,
          description: data.description || '',
          api_base_url: data.api_base_url || '',
          api_key: '',
          api_secret: '',
          supported_zones: Array.isArray(data.supported_zones) ? data.supported_zones.join(', ') : (data.supported_zones || ''),
          is_active: data.is_active !== false
        });
      } else if (type === 'jurisdiction') {
        setFormData({
          name: data.name,
          country: data.country,
          zone_code: data.zone_code,
          provider_id: data.provider_id,
          description: data.description || '',
          government_fee: data.government_fee || 0,
          service_fee: data.service_fee || 0,
          currency: data.currency || 'USD',
          processing_time_days: data.processing_time_days || '',
          is_active: data.is_active !== false,
          display_order: data.display_order || 0
        });
      } else if (type === 'activity') {
        setFormData({
          code: data.code,
          name: data.name,
          description: data.description || '',
          jurisdiction_id: data.jurisdiction_id,
          is_active: data.is_active !== false
        });
      }
    } else {
      // Create mode
      if (type === 'provider') {
        setFormData({ name: '', code: '', description: '', api_base_url: '', api_key: '', api_secret: '', supported_zones: '', is_active: true });
      } else if (type === 'jurisdiction') {
        setFormData({ name: '', country: '', zone_code: '', provider_id: providers[0]?.id || '', description: '', government_fee: 0, service_fee: 0, currency: 'USD', processing_time_days: '', is_active: true, display_order: 0 });
      } else if (type === 'activity') {
        setFormData({ code: '', name: '', description: '', jurisdiction_id: selectedJurisdiction?.id || '', is_active: true });
      }
    }
    setModal({ isOpen: true, type, mode, data });
  };

  const closeModal = () => {
    setModal({ isOpen: false, type: '', mode: '', data: null });
    setFormData({});
    setFormError('');
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    try {
      if (modal.type === 'provider') {
        const payload = {
          name: formData.name,
          code: formData.code,
          description: formData.description,
          apiBaseUrl: formData.api_base_url,
          supportedZones: formData.supported_zones.split(',').map(s => s.trim()).filter(Boolean),
          isActive: formData.is_active
        };
        if (formData.api_key) payload.apiKey = formData.api_key;
        if (formData.api_secret) payload.apiSecret = formData.api_secret;

        if (modal.mode === 'create') {
          await businessAPI.createProvider(payload);
        } else {
          await businessAPI.updateProvider(modal.data.id, payload);
        }
        fetchProviders();
      } else if (modal.type === 'jurisdiction') {
        const payload = { ...formData };
        if (modal.mode === 'create') {
          await businessAPI.createJurisdiction(payload);
        } else {
          await businessAPI.updateJurisdiction(modal.data.id, payload);
        }
        fetchJurisdictions();
      } else if (modal.type === 'activity') {
        const payload = { ...formData };
        if (modal.mode === 'create') {
          await businessAPI.createActivity(payload);
        } else {
          await businessAPI.updateActivity(modal.data.id, payload);
        }
        fetchActivities();
      }
      closeModal();
    } catch (error) {
      console.error('Form submit error:', error);
      setFormError(error.response?.data?.message || 'Failed to save. Please try again.');
    }
  };

  const handleDeleteJurisdiction = async (id) => {
    if (!window.confirm('Are you sure you want to delete this jurisdiction?')) return;
    try {
      await businessAPI.deleteJurisdiction(id);
      fetchJurisdictions();
    } catch (error) {
      console.error('Failed to delete jurisdiction:', error);
    }
  };

  const handleDeleteActivity = async (id) => {
    if (!window.confirm('Are you sure you want to delete this activity code?')) return;
    try {
      await businessAPI.deleteActivity(id);
      fetchActivities();
    } catch (error) {
      console.error('Failed to delete activity:', error);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">BUSINESS SETTINGS</h1>
            <p className="page-subtitle">MANAGE PROVIDERS, JURISDICTIONS & ACTIVITIES</p>
          </div>
        </div>

        <div className="dashboard-content">
          {/* Tab Navigation */}
          <div className="tab-nav">
            <button
              className={`tab-btn ${activeTab === 'providers' ? 'active' : ''}`}
              onClick={() => setActiveTab('providers')}
            >
              Providers
            </button>
            <button
              className={`tab-btn ${activeTab === 'jurisdictions' ? 'active' : ''}`}
              onClick={() => setActiveTab('jurisdictions')}
            >
              Jurisdictions
            </button>
            <button
              className={`tab-btn ${activeTab === 'activities' ? 'active' : ''}`}
              onClick={() => setActiveTab('activities')}
            >
              Activity Codes
            </button>
          </div>

          {/* Providers Tab */}
          {activeTab === 'providers' && (
            <div className="dashboard-section">
              <div className="section-header">
                <h3 className="section-title">Registration Providers</h3>
                <button className="btn-add" onClick={() => openModal('provider', 'create')}>
                  + Add Provider
                </button>
              </div>

              {providersLoading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading providers...</p>
                </div>
              ) : providers.length === 0 ? (
                <div className="empty-state">
                  <p>No providers configured</p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>NAME</th>
                        <th>CODE</th>
                        <th>ZONES</th>
                        <th>STATUS</th>
                        <th>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {providers.map((p) => (
                        <tr key={p.id}>
                          <td>
                            <div className="applicant-info">
                              <span className="applicant-name">{p.name}</span>
                              <span className="applicant-email">{p.description || '-'}</span>
                            </div>
                          </td>
                          <td><span className="biz-code-badge">{p.code}</span></td>
                          <td>
                            {(Array.isArray(p.supported_zones) ? p.supported_zones : []).map((z) => (
                              <span key={z} className="biz-zone-badge">{z}</span>
                            ))}
                          </td>
                          <td>
                            <span className={`biz-status-pill ${p.is_active ? 'active' : 'inactive'}`}>
                              {p.is_active ? 'ACTIVE' : 'INACTIVE'}
                            </span>
                          </td>
                          <td>
                            <button className="btn-view-application" onClick={() => openModal('provider', 'edit', p)}>
                              EDIT
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Jurisdictions Tab */}
          {activeTab === 'jurisdictions' && (
            <div className="dashboard-section">
              <div className="section-header">
                <h3 className="section-title">Jurisdictions</h3>
                <button className="btn-add" onClick={() => openModal('jurisdiction', 'create')}>
                  + Add Jurisdiction
                </button>
              </div>

              {jurisdictionsLoading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading jurisdictions...</p>
                </div>
              ) : jurisdictions.length === 0 ? (
                <div className="empty-state">
                  <p>No jurisdictions configured</p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>NAME</th>
                        <th>COUNTRY</th>
                        <th>PROVIDER</th>
                        <th>FEES</th>
                        <th>STATUS</th>
                        <th>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jurisdictions.map((j) => (
                        <tr key={j.id}>
                          <td>
                            <div className="applicant-info">
                              <span className="applicant-name">{j.name}</span>
                              <span className="applicant-email">{j.zone_code}</span>
                            </div>
                          </td>
                          <td>{j.country}</td>
                          <td>{providers.find(p => p.id === j.provider_id)?.name || j.provider_id}</td>
                          <td>
                            <div className="biz-fee-info">
                              <span>Gov: {formatCurrency(j.government_fee, j.currency)}</span>
                              <span>Svc: {formatCurrency(j.service_fee, j.currency)}</span>
                            </div>
                          </td>
                          <td>
                            <span className={`biz-status-pill ${j.is_active ? 'active' : 'inactive'}`}>
                              {j.is_active ? 'ACTIVE' : 'INACTIVE'}
                            </span>
                          </td>
                          <td>
                            <div className="actions-cell">
                              <button className="btn-view-application" onClick={() => openModal('jurisdiction', 'edit', j)}>
                                EDIT
                              </button>
                              <button className="btn-delete-small" onClick={() => handleDeleteJurisdiction(j.id)}>
                                DELETE
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Activities Tab */}
          {activeTab === 'activities' && (
            <div className="dashboard-section">
              <div className="section-header">
                <h3 className="section-title">Activity Codes</h3>
                <div className="section-header-right">
                  <div className="custom-dropdown" ref={jurisdictionDropdownRef}>
                    <button
                      className="custom-dropdown-trigger"
                      onClick={() => setJurisdictionDropdownOpen(!jurisdictionDropdownOpen)}
                    >
                      <span>{selectedJurisdiction?.name || 'Select Jurisdiction'}</span>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    {jurisdictionDropdownOpen && (
                      <div className="custom-dropdown-menu">
                        {jurisdictions.map((j) => (
                          <div
                            key={j.id}
                            className={`custom-dropdown-item ${selectedJurisdiction?.id === j.id ? 'active' : ''}`}
                            onClick={() => {
                              setSelectedJurisdiction(j);
                              setJurisdictionDropdownOpen(false);
                            }}
                          >
                            {j.name} ({j.country})
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button className="btn-add" onClick={() => openModal('activity', 'create')}>
                    + Add Activity
                  </button>
                </div>
              </div>

              {activitiesLoading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading activities...</p>
                </div>
              ) : !selectedJurisdiction ? (
                <div className="empty-state">
                  <p>Select a jurisdiction to view activity codes</p>
                </div>
              ) : activities.length === 0 ? (
                <div className="empty-state">
                  <p>No activity codes for this jurisdiction</p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>CODE</th>
                        <th>NAME</th>
                        <th>DESCRIPTION</th>
                        <th>STATUS</th>
                        <th>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activities.map((a) => (
                        <tr key={a.id}>
                          <td><span className="biz-code-badge">{a.code}</span></td>
                          <td>{a.name}</td>
                          <td className="biz-desc-cell">{a.description || '-'}</td>
                          <td>
                            <span className={`biz-status-pill ${a.is_active ? 'active' : 'inactive'}`}>
                              {a.is_active ? 'ACTIVE' : 'INACTIVE'}
                            </span>
                          </td>
                          <td>
                            <div className="actions-cell">
                              <button className="btn-view-application" onClick={() => openModal('activity', 'edit', a)}>
                                EDIT
                              </button>
                              <button className="btn-delete-small" onClick={() => handleDeleteActivity(a.id)}>
                                DELETE
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Create/Edit Modal */}
      {modal.isOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {modal.mode === 'create' ? 'Create' : 'Edit'}{' '}
                {modal.type === 'provider' ? 'Provider' : modal.type === 'jurisdiction' ? 'Jurisdiction' : 'Activity Code'}
              </h2>
              <button className="modal-close" onClick={closeModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <form className="modal-body" onSubmit={handleFormSubmit}>
              {formError && (
                <div className="form-error">{formError}</div>
              )}

              {/* Provider Form */}
              {modal.type === 'provider' && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Name *</label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => handleFormChange('name', e.target.value)}
                        placeholder="e.g., DAZ"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Code *</label>
                      <input
                        type="text"
                        value={formData.code || ''}
                        onChange={(e) => handleFormChange('code', e.target.value)}
                        placeholder="e.g., daz"
                        required
                        disabled={modal.mode === 'edit'}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => handleFormChange('description', e.target.value)}
                      placeholder="Provider description"
                      rows={2}
                    />
                  </div>
                  <div className="form-group">
                    <label>API Base URL</label>
                    <input
                      type="text"
                      value={formData.api_base_url || ''}
                      onChange={(e) => handleFormChange('api_base_url', e.target.value)}
                      placeholder="https://api.example.com"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>API Key {modal.mode === 'edit' ? '(leave blank to keep)' : ''}</label>
                      <input
                        type="password"
                        value={formData.api_key || ''}
                        onChange={(e) => handleFormChange('api_key', e.target.value)}
                        placeholder="API key"
                      />
                    </div>
                    <div className="form-group">
                      <label>API Secret {modal.mode === 'edit' ? '(leave blank to keep)' : ''}</label>
                      <input
                        type="password"
                        value={formData.api_secret || ''}
                        onChange={(e) => handleFormChange('api_secret', e.target.value)}
                        placeholder="API secret"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Supported Zones (comma separated)</label>
                      <input
                        type="text"
                        value={formData.supported_zones || ''}
                        onChange={(e) => handleFormChange('supported_zones', e.target.value)}
                        placeholder="PROSPERA, ST_KITTS"
                      />
                    </div>
                    <div className="form-group">
                      <label>Status</label>
                      <div className="form-toggle">
                        <label className="toggle-label">
                          <input
                            type="checkbox"
                            checked={formData.is_active}
                            onChange={(e) => handleFormChange('is_active', e.target.checked)}
                          />
                          <span>{formData.is_active ? 'Active' : 'Inactive'}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Jurisdiction Form */}
              {modal.type === 'jurisdiction' && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Name *</label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => handleFormChange('name', e.target.value)}
                        placeholder="e.g., Prospera ZEDE"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Country *</label>
                      <input
                        type="text"
                        value={formData.country || ''}
                        onChange={(e) => handleFormChange('country', e.target.value)}
                        placeholder="e.g., Honduras"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Zone Code *</label>
                      <input
                        type="text"
                        value={formData.zone_code || ''}
                        onChange={(e) => handleFormChange('zone_code', e.target.value)}
                        placeholder="e.g., PROSPERA"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Provider *</label>
                      <select
                        value={formData.provider_id || ''}
                        onChange={(e) => handleFormChange('provider_id', parseInt(e.target.value))}
                        required
                      >
                        <option value="">Select provider</option>
                        {providers.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => handleFormChange('description', e.target.value)}
                      placeholder="Jurisdiction description"
                      rows={2}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Embassy Fees</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.government_fee || 0}
                        onChange={(e) => handleFormChange('government_fee', parseFloat(e.target.value))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Naytive Service Fees</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.service_fee || 0}
                        onChange={(e) => handleFormChange('service_fee', parseFloat(e.target.value))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Currency</label>
                      <input
                        type="text"
                        value={formData.currency || 'USD'}
                        onChange={(e) => handleFormChange('currency', e.target.value)}
                        maxLength={3}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Processing Time (days)</label>
                      <input
                        type="number"
                        value={formData.processing_time_days || ''}
                        onChange={(e) => handleFormChange('processing_time_days', parseInt(e.target.value) || '')}
                        placeholder="14"
                      />
                    </div>
                    <div className="form-group">
                      <label>Display Order</label>
                      <input
                        type="number"
                        value={formData.display_order || 0}
                        onChange={(e) => handleFormChange('display_order', parseInt(e.target.value) || 0)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Status</label>
                      <div className="form-toggle">
                        <label className="toggle-label">
                          <input
                            type="checkbox"
                            checked={formData.is_active}
                            onChange={(e) => handleFormChange('is_active', e.target.checked)}
                          />
                          <span>{formData.is_active ? 'Active' : 'Inactive'}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Activity Code Form */}
              {modal.type === 'activity' && (
                <>
                  <div className="form-group">
                    <label>Jurisdiction</label>
                    <select
                      value={formData.jurisdiction_id || ''}
                      onChange={(e) => handleFormChange('jurisdiction_id', parseInt(e.target.value))}
                      required
                    >
                      <option value="">Select jurisdiction</option>
                      {jurisdictions.map((j) => (
                        <option key={j.id} value={j.id}>{j.name} ({j.country})</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Code *</label>
                      <input
                        type="text"
                        value={formData.code || ''}
                        onChange={(e) => handleFormChange('code', e.target.value)}
                        placeholder="e.g., SOFTWARE_DEVELOPMENT"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Name *</label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => handleFormChange('name', e.target.value)}
                        placeholder="e.g., Software Development"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => handleFormChange('description', e.target.value)}
                      placeholder="Activity description"
                      rows={2}
                    />
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <div className="form-toggle">
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => handleFormChange('is_active', e.target.checked)}
                        />
                        <span>{formData.is_active ? 'Active' : 'Inactive'}</span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  {modal.mode === 'create' ? 'Create' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessSettings;
